import { navigateTo } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import type { Market } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { PAGE_NAME_BY_PLP_UUID } from '~/scopes/cms/service/content-service.config'
import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { ROUTES as PRODUCT_LIST_ROUTES } from './route-names'

export default ({ market }: { market: Market }): RouteRecordRaw[] => {
  const experiments = useExperiments()
  const isNewSellerPageEnabled =
    experiments['experiment.sellerPageWeb'] === 'newSellerPage'

  const availableUniverseUUID = PAGE_NAME_BY_PLP_UUID[market.countryCode]
  const universeRegex = availableUniverseUUID
    ? Object.keys(availableUniverseUUID).join('|')
    : '__no_universe_page__'

  const universeRoute: RouteRecordRaw = {
    name: PRODUCT_LIST_ROUTES.UNIVERSE,
    component: () => import('~/scopes/cms/Landing.vue'),
    path: `l/:slugV2/:uuid(${universeRegex})`,
    meta: {
      owners: ['bot-squad-search-and-recommendation-front'],
      middleware: [redirectIfWebview],
      cache: 'public, max-age=90000',
    },
  }

  const sellerProductListRoute: RouteRecordRaw = {
    path: 's/:merchantName/:uuid',
    name: PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST,
    meta: {
      owners: ['bot-squad-search-and-recommendation-front'],
      middleware: [redirectIfWebview],
      cache: 'public, max-age=300',
    },
    component: () =>
      isNewSellerPageEnabled
        ? import('~/scopes/product-list/pages/SellerPage/SellerPage.vue')
        : import(
            '~/scopes/product-list/pages/SellerProductListPage/SellerProductListPage.vue'
          ),
  }

  return [
    {
      path: 'search',
      name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
      meta: {
        owners: ['bot-squad-search-and-recommendation-front'],
      },
      component: () =>
        import(
          '~/scopes/product-list/pages/SearchResultPage/SearchResultPage.vue'
        ),
    },
    {
      path: 'l/:slugV2/:uuid',
      name: PRODUCT_LIST_ROUTES.PRODUCT_LIST,
      meta: {
        owners: ['bot-squad-search-and-recommendation-front'],
        middleware: (to) => {
          const uuid = Array.isArray(to.params.uuid)
            ? to.params.uuid[0]
            : to.params.uuid

          if (availableUniverseUUID && uuid in availableUniverseUUID) {
            return navigateTo({
              name: PRODUCT_LIST_ROUTES.UNIVERSE,
              params: to.params,
              query: to.query,
              hash: to.hash,
            })
          }

          return undefined
        },
        cache: 'public, max-age=90000',
      },
      component: () =>
        import(
          '~/scopes/product-list/pages/ProductListPage/ProductListPage.vue'
        ),
    },
    sellerProductListRoute,
    universeRoute,
    ...generateWebviewRoutes([sellerProductListRoute, universeRoute]),
  ]
}
